import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Tooltip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  Add,
  Close,
  Delete,
  Edit,
  LocationCity,
  MyLocation,
  Search,
  GpsFixed,
  WarningAmber,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useMaterialUIController } from "context";
import { handleAlert } from "redux/festures/alertSlice";
import {
  fetchAllCities,
  addCity,
  editCity,
  toggleCityStatus,
  removeCity,
} from "redux/festures/cityCRUD";

// ─── Reusable Dialog Shell ────────────────────────────────────────────────────
const CityDialog = ({ open, onClose, title, children, darkMode }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: 3,
        overflow: "hidden",
        backgroundColor: darkMode ? "#1a2035 !important" : "#ffffff !important",
        backgroundImage: "none !important",
        boxShadow: "0 20px 40px rgba(0,0,0,0.3) !important",
      },
    }}
  >
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
        color: "#fff",
        py: 1.8,
        px: 3,
      }}
    >
      <MDTypography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
        {title}
      </MDTypography>
      <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small">
        <Close fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent
      sx={{
        pt: "20px !important",
        px: 3,
        pb: 3,
        backgroundColor: darkMode ? "#1a2035" : "#ffffff",
      }}
    >
      {children}
    </DialogContent>
  </Dialog>
);

CityDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  darkMode: PropTypes.bool,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ManageCity = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const dispatch = useDispatch();
  const admin = localStorage.getItem("admin_id");

  const { cities, loading, mutating } = useSelector((state) => state.isCityManager);

  // ── Dialog states
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // ── Form states
  const [cityName, setCityName] = useState("");
  const [cityId, setCityId] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);

  const [selectedCity, setSelectedCity] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ── Fetch on mount
  useEffect(() => {
    if (admin) {
      dispatch(fetchAllCities(admin));
    }
  }, [admin, dispatch]);

  // ── Helpers
  const resetForm = () => {
    setCityName("");
    setCityId("");
    setLatitude("");
    setLongitude("");
    setSelectedCity(null);
    setGeoLoading(false);
  };

  const showAlert = (success, msg) =>
    dispatch(handleAlert({ isOpen: true, type: success ? "success" : "error", msg }));

  // ── Refresh list
  const refresh = () => dispatch(fetchAllCities(admin));

  // ── Auto-fetch coordinates from OpenStreetMap Nominatim
  const handleAutoFetchCoords = async () => {
    if (!cityName.trim() && !cityId.toString().trim()) {
      showAlert(false, "Please enter a City Name or Pincode first");
      return;
    }
    setGeoLoading(true);
    try {
      const query = `${cityName.trim()} ${cityId.toString().trim()} India`.trim();
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`
      );
      const data = await res.json();
      if (data && data.length > 0) {
        setLatitude(parseFloat(data[0].lat).toFixed(6));
        setLongitude(parseFloat(data[0].lon).toFixed(6));
        showAlert(true, `GPS coordinates found: ${data[0].display_name.split(",")[0]}`);
      } else {
        showAlert(false, "Could not find exact coordinates. Please enter manually.");
      }
    } catch (err) {
      showAlert(false, "Failed to auto-fetch coordinates. Please enter manually.");
    } finally {
      setGeoLoading(false);
    }
  };

  // ── Get Current Device Location
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      showAlert(false, "Geolocation is not supported by your browser");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude.toFixed(6));
        setLongitude(pos.coords.longitude.toFixed(6));
        showAlert(true, "Current GPS coordinates captured successfully!");
        setGeoLoading(false);
      },
      (err) => {
        showAlert(false, "Failed to get current location: " + err.message);
        setGeoLoading(false);
      }
    );
  };

  // ── Add city
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!cityName.trim() || !cityId.toString().trim()) {
      showAlert(false, "City name and City ID (Pincode) are required");
      return;
    }
    const payload = {
      cityName: cityName.trim(),
      cityId: Number(cityId),
    };
    if (latitude !== "" && longitude !== "") {
      payload.latitude = Number(latitude);
      payload.longitude = Number(longitude);
    }

    const res = await dispatch(
      addCity({
        adminId: admin,
        data: payload,
      })
    );
    showAlert(res?.payload?.success, res?.payload?.message);
    if (res?.payload?.success) {
      setAddOpen(false);
      resetForm();
      refresh();
    }
  };

  // ── Update city
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!cityName.trim() || !cityId.toString().trim()) {
      showAlert(false, "City name and City ID (Pincode) are required");
      return;
    }
    const payload = {
      cityName: cityName.trim(),
      cityId: cityId.toString(),
    };
    if (latitude !== "" && longitude !== "") {
      payload.latitude = Number(latitude);
      payload.longitude = Number(longitude);
    }

    const res = await dispatch(
      editCity({
        cityId: selectedCity?._id,
        adminId: admin,
        data: payload,
      })
    );
    showAlert(res?.payload?.success, res?.payload?.message);
    if (res?.payload?.success) {
      setEditOpen(false);
      resetForm();
      refresh();
    }
  };

  // ── Toggle
  const handleToggle = async (city) => {
    setTogglingId(city._id);
    const res = await dispatch(
      toggleCityStatus({ cityId: city._id, adminId: admin })
    );
    showAlert(res?.payload?.success, res?.payload?.message);
    if (res?.payload?.success) refresh();
    setTogglingId(null);
  };

  // ── Delete
  const handleDelete = async () => {
    setDeletingId(selectedCity?._id);
    const res = await dispatch(
      removeCity({ cityId: selectedCity?._id, adminId: admin })
    );
    showAlert(res?.payload?.success, res?.payload?.message);
    setDeleteOpen(false);
    setDeletingId(null);
    resetForm();
    if (res?.payload?.success) refresh();
  };

  // ─── Row skeleton
  const RowSkeleton = () => (
    <MDBox
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 3,
        py: 1.8,
        borderBottom: "1px solid #f0f0f5",
      }}
    >
      {[40, 120, 80, 140, 60, 60, 60].map((w, i) => (
        <Skeleton key={i} variant="rounded" width={w} height={20} sx={{ flexShrink: 0 }} />
      ))}
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(26,35,126,0.08)",
          }}
        >
          {/* ── Header ───────────────────────────────────────── */}
          <MDBox
            sx={{
              background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
              px: 3,
              py: 2.2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <LocationCity sx={{ color: "#fff", fontSize: 26 }} />
              <MDTypography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
                Manage City
              </MDTypography>
            </MDBox>
            <MDButton
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => { resetForm(); setAddOpen(true); }}
              sx={{
                backgroundColor: "#fff",
                color: "#1a237e",
                fontWeight: "bold",
                borderRadius: "10px",
                px: 2,
                "&:hover": { backgroundColor: "#e8eaf6" },
              }}
            >
              Add City
            </MDButton>
          </MDBox>

          {/* ── Table header ─────────────────────────────────── */}
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: "50px 1.2fr 110px 1.5fr 140px 80px 80px",
              px: 3,
              py: 1.5,
              backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "#f5f6fa",
              borderBottom: darkMode ? "1px solid rgba(255,255,255,0.15)" : "1px solid #e8e8f0",
              alignItems: "center",
            }}
          >
            {["S.No", "City Name", "Postal / Pin", "GPS Coordinates (Lat, Lng)", "Status", "Edit", "Delete"].map((h) => (
              <MDTypography
                key={h}
                variant="caption"
                sx={{ fontWeight: "bold", color: darkMode ? "#fff" : "#1a237e", textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                {h}
              </MDTypography>
            ))}
          </MDBox>

          {/* ── Rows ─────────────────────────────────────────── */}
          {loading ? (
            [1, 2, 3, 4, 5].map((i) => <RowSkeleton key={i} />)
          ) : cities && cities.length > 0 ? (
            cities.map((city, index) => {
              const coords = city?.location?.coordinates || [];
              const lng = coords[0];
              const lat = coords[1];
              const hasValidCoords = lat !== undefined && lng !== undefined && (lat !== 0 || lng !== 0);

              return (
                <MDBox
                  key={city._id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "50px 1.2fr 110px 1.5fr 140px 80px 80px",
                    px: 3,
                    py: 1.8,
                    alignItems: "center",
                    borderBottom: darkMode ? "1px solid rgba(255,255,255,0.15)" : "1px solid #f0f0f5",
                    transition: "background 0.15s",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  {/* S.No */}
                  <MDTypography variant="body2" sx={{ color: "#9e9e9e", fontWeight: "medium" }}>
                    {index + 1}
                  </MDTypography>

                  {/* Name */}
                  <MDTypography variant="body2" sx={{ color: darkMode ? "#fff" : "#1a237e", fontWeight: "bold" }}>
                    {city?.cityName || "—"}
                  </MDTypography>

                  {/* City ID / Pincode */}
                  <MDTypography variant="body2" sx={{ color: darkMode ? "rgba(255,255,255,0.7)" : "#616161", fontWeight: "medium" }}>
                    {city?.cityId || "—"}
                  </MDTypography>

                  {/* GPS Coordinates */}
                  <MDBox display="flex" alignItems="center" gap={1}>
                    {hasValidCoords ? (
                      <MDBox
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: "6px",
                          backgroundColor: darkMode ? "rgba(76, 175, 80, 0.15)" : "#e8f5e9",
                          color: "#2e7d32",
                          border: "1px solid #c8e6c9",
                        }}
                      >
                        <GpsFixed sx={{ fontSize: 14 }} />
                        <MDTypography variant="caption" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                          {lat}, {lng}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <MDBox
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: "6px",
                          backgroundColor: darkMode ? "rgba(255, 152, 0, 0.15)" : "#fff3e0",
                          color: "#e65100",
                          border: "1px solid #ffe0b2",
                        }}
                      >
                        <WarningAmber sx={{ fontSize: 14 }} />
                        <MDTypography variant="caption" sx={{ fontWeight: "bold", color: "#e65100" }}>
                          GPS Missing (0, 0)
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>

                  {/* Toggle */}
                  <MDBox display="flex" alignItems="center" gap={1}>
                    {togglingId === city._id ? (
                      <CircularProgress size={20} color="info" />
                    ) : (
                      <Tooltip title={city?.disable ? "Mark Active" : "Mark Inactive"}>
                        <Switch
                          checked={!city?.disable}
                          color="info"
                          size="small"
                          onChange={() => handleToggle(city)}
                          inputProps={{ "aria-label": "toggle city status" }}
                        />
                      </Tooltip>
                    )}
                    <MDTypography
                      variant="caption"
                      sx={{ color: !city?.disable ? "#43a047" : "#e53935", fontWeight: "bold" }}
                    >
                      {!city?.disable ? "Active" : "Inactive"}
                    </MDTypography>
                  </MDBox>

                  {/* Update */}
                  <Tooltip title="Edit City & Coordinates">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedCity(city);
                        setCityName(city?.cityName || "");
                        setCityId(city?.cityId || "");
                        const c = city?.location?.coordinates || [];
                        setLongitude(c[0] !== undefined && c[0] !== 0 ? c[0] : "");
                        setLatitude(c[1] !== undefined && c[1] !== 0 ? c[1] : "");
                        setEditOpen(true);
                      }}
                      sx={{
                        color: "#1a237e",
                        border: "1px solid #c5cae9",
                        borderRadius: "8px",
                        p: 0.7,
                        "&:hover": { backgroundColor: "#e8eaf6" },
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Delete */}
                  <Tooltip title="Delete City">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedCity(city);
                        setDeleteOpen(true);
                      }}
                      sx={{
                        color: "#c62828",
                        border: "1px solid #ffcdd2",
                        borderRadius: "8px",
                        p: 0.7,
                        "&:hover": { backgroundColor: "#ffebee" },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              );
            })
          ) : (
            <MDBox py={6} display="flex" flexDirection="column" alignItems="center" gap={1}>
              <LocationCity sx={{ fontSize: 48, color: "#c5cae9" }} />
              <MDTypography variant="body2" sx={{ color: "#9e9e9e" }}>
                No cities found. Add your first city!
              </MDTypography>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />

      {/* ─── Add City Dialog ─────────────────────────────────── */}
      <CityDialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          resetForm();
        }}
        title="Add New City & GPS Coordinates"
        darkMode={darkMode}
      >
        <MDBox
          component="form"
          onSubmit={handleAdd}
          display="flex"
          flexDirection="column"
          gap={2.2}
          mt={1}
        >
          <MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: darkMode ? "#90caf9" : "#1a237e",
                fontWeight: "bold",
                mb: 0.5,
                display: "block",
              }}
            >
              City Name *
            </MDTypography>
            <MDInput
              fullWidth
              required
              placeholder="e.g. Bhopal"
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              disabled={mutating}
              sx={{
                input: {
                  color: darkMode ? "#ffffff !important" : "#111827 !important",
                  fontWeight: 500,
                },
              }}
            />
          </MDBox>

          <MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: darkMode ? "#90caf9" : "#1a237e",
                fontWeight: "bold",
                mb: 0.5,
                display: "block",
              }}
            >
              City Postal / Pin Code *
            </MDTypography>
            <MDInput
              fullWidth
              required
              type="number"
              placeholder="e.g. 462001"
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              disabled={mutating}
              sx={{
                input: {
                  color: darkMode ? "#ffffff !important" : "#111827 !important",
                  fontWeight: 500,
                },
              }}
            />
          </MDBox>

          {/* GPS Coordinate Auto-Fetch Controls */}
          <MDBox
            p={1.5}
            borderRadius="8px"
            sx={{
              backgroundColor: darkMode ? "rgba(255,255,255,0.04)" : "#f8f9fa",
              border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e9ecef",
            }}
          >
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <MDTypography variant="caption" sx={{ fontWeight: "bold", color: darkMode ? "#90caf9" : "#1a237e" }}>
                GPS Coordinates (Strict Geo-fencing)
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handleAutoFetchCoords}
                  disabled={geoLoading || mutating}
                  startIcon={geoLoading ? <CircularProgress size={14} color="inherit" /> : <Search />}
                  sx={{ py: 0.4, px: 1, fontSize: "0.7rem" }}
                >
                  Auto-Detect
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleGetCurrentLocation}
                  disabled={geoLoading || mutating}
                  startIcon={<MyLocation />}
                  sx={{ py: 0.4, px: 1, fontSize: "0.7rem" }}
                >
                  My GPS
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
              <MDBox>
                <MDTypography variant="caption" sx={{ color: "#757575", mb: 0.3, display: "block" }}>
                  Latitude (e.g. 23.259933)
                </MDTypography>
                <MDInput
                  fullWidth
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  disabled={mutating}
                />
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" sx={{ color: "#757575", mb: 0.3, display: "block" }}>
                  Longitude (e.g. 77.412613)
                </MDTypography>
                <MDInput
                  fullWidth
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  disabled={mutating}
                />
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" gap={1.5} pt={1}>
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => {
                setAddOpen(false);
                resetForm();
              }}
              disabled={mutating}
            >
              Cancel
            </MDButton>
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              size="small"
              disabled={mutating}
              sx={{ minWidth: 110 }}
            >
              {mutating ? <CircularProgress size={18} color="inherit" /> : "Add City"}
            </MDButton>
          </MDBox>
        </MDBox>
      </CityDialog>

      {/* ─── Edit City Dialog ─────────────────────────────────── */}
      <CityDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          resetForm();
        }}
        title="Update City & GPS Coordinates"
        darkMode={darkMode}
      >
        <MDBox
          component="form"
          onSubmit={handleUpdate}
          display="flex"
          flexDirection="column"
          gap={2.2}
          mt={1}
        >
          <MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: darkMode ? "#90caf9" : "#1a237e",
                fontWeight: "bold",
                mb: 0.5,
                display: "block",
              }}
            >
              City Name *
            </MDTypography>
            <MDInput
              fullWidth
              required
              placeholder="e.g. Bhopal"
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              disabled={mutating}
              sx={{
                input: {
                  color: darkMode ? "#ffffff !important" : "#111827 !important",
                  fontWeight: 500,
                },
              }}
            />
          </MDBox>

          <MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: darkMode ? "#90caf9" : "#1a237e",
                fontWeight: "bold",
                mb: 0.5,
                display: "block",
              }}
            >
              City Postal / Pin Code *
            </MDTypography>
            <MDInput
              fullWidth
              required
              placeholder="e.g. 462001"
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              disabled={mutating}
              sx={{
                input: {
                  color: darkMode ? "#ffffff !important" : "#111827 !important",
                  fontWeight: 500,
                },
              }}
            />
          </MDBox>

          {/* GPS Coordinate Auto-Fetch Controls for Edit */}
          <MDBox
            p={1.5}
            borderRadius="8px"
            sx={{
              backgroundColor: darkMode ? "rgba(255,255,255,0.04)" : "#f8f9fa",
              border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e9ecef",
            }}
          >
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <MDTypography variant="caption" sx={{ fontWeight: "bold", color: darkMode ? "#90caf9" : "#1a237e" }}>
                GPS Coordinates (Strict Geo-fencing)
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handleAutoFetchCoords}
                  disabled={geoLoading || mutating}
                  startIcon={geoLoading ? <CircularProgress size={14} color="inherit" /> : <Search />}
                  sx={{ py: 0.4, px: 1, fontSize: "0.7rem" }}
                >
                  Auto-Detect
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleGetCurrentLocation}
                  disabled={geoLoading || mutating}
                  startIcon={<MyLocation />}
                  sx={{ py: 0.4, px: 1, fontSize: "0.7rem" }}
                >
                  My GPS
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
              <MDBox>
                <MDTypography variant="caption" sx={{ color: "#757575", mb: 0.3, display: "block" }}>
                  Latitude (e.g. 23.259933)
                </MDTypography>
                <MDInput
                  fullWidth
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  disabled={mutating}
                />
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" sx={{ color: "#757575", mb: 0.3, display: "block" }}>
                  Longitude (e.g. 77.412613)
                </MDTypography>
                <MDInput
                  fullWidth
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  disabled={mutating}
                />
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" gap={1.5} pt={1}>
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => {
                setEditOpen(false);
                resetForm();
              }}
              disabled={mutating}
            >
              Cancel
            </MDButton>
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              size="small"
              disabled={mutating}
              sx={{ minWidth: 110 }}
            >
              {mutating ? <CircularProgress size={18} color="inherit" /> : "Update City"}
            </MDButton>
          </MDBox>
        </MDBox>
      </CityDialog>

      {/* ─── Delete Confirm Dialog ────────────────────────────── */}
      <CityDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          resetForm();
        }}
        title="Delete City"
        darkMode={darkMode}
      >
        <MDBox display="flex" flexDirection="column" gap={2.5} mt={1}>
          <MDTypography
            variant="body2"
            sx={{
              color: darkMode ? "#e0e0e0" : "#424242",
              textAlign: "center",
            }}
          >
            Are you sure you want to delete{" "}
            <strong style={{ color: "#c62828" }}>{selectedCity?.cityName}</strong>?
            <br />
            <span style={{ color: "#9e9e9e", fontSize: "0.8rem" }}>This action cannot be undone.</span>
          </MDTypography>
          <MDBox display="flex" justifyContent="center" gap={2} pt={1}>
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              sx={{ minWidth: 100 }}
              onClick={() => {
                setDeleteOpen(false);
                resetForm();
              }}
              disabled={mutating}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="error"
              size="small"
              sx={{ minWidth: 100 }}
              onClick={handleDelete}
              disabled={mutating}
            >
              {mutating ? <CircularProgress size={18} color="inherit" /> : "Delete"}
            </MDButton>
          </MDBox>
        </MDBox>
      </CityDialog>
    </DashboardLayout>
  );
};

export default ManageCity;
